import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
// Constants - Functions
import { WEB_SERVICE_MIMS, protocols } from "../../../../config/constants";
import { getValidProtocols } from "../../../../config/validateProtocols";
import { GetAppBasicProtocolData } from "../getProtocolData/appBasics";
import { GetAppRotationProtocolData } from "../getProtocolData/appRotation";
import { GetAppARProtocolData } from "../getProtocolData/appAR";
import { GetHandTrackingProtocolData } from "../getProtocolData/webHandTracking";
import { GetFullBodyProtocolData } from "../getProtocolData/webFullBody";
// Components
import UseFetch from "../../../../config/useFetch";
import PrescriptionsHeader from "../prescriptionsHeader";
import ProtocolPrescription from "../protocolPrescription";

const PlusProtocols = ({
  software,
  prescriptions,
  prescriptionsToDatabase,
  hospitalProtocols,
  protocolsSessionsNumber,
  isDisabledInputs,
  minutesUsage,
  platformFilter,
  setPlatformFilter,
  protocolControlsActions,
  rechargeProtocolControls,
  setRechargeProtocolControls,
  isIncompatibleWithAR,
}) => {
  const [protocolsSupportCompatibility, setProtocolsSupportCompatibility] = useState([]);
  const { patientId } = useParams();
  const [t] = useTranslation("global");

  // Fetching protocols support compatibility
  useEffect(() => {
    UseFetch("get", `${WEB_SERVICE_MIMS}/protocols/get-support-compatibility`)
      .then(({ data }) => {
        setProtocolsSupportCompatibility(data.map((protocol) => protocol.PROTOCOL_ID));
      })
      .catch((error) => {
        console.log("Get protocols support compatibility: ", error);
      });

    return () => {
      setProtocolsSupportCompatibility([]);
    };
  }, []);

  const getProtocolPrescriptions = (protocolId) => {
    return prescriptions.filter((prescription) => prescription.PROTOCOL_ID === protocolId);
  };

  const filterProtocolsByPlatform = (protocols) => {
    if (!platformFilter) return protocols;

    return protocols.sort((a, b) => {
      if (a.PLATFORMS.includes(platformFilter)) return -1;
      if (b.PLATFORMS.includes(platformFilter)) return 1;
      return 0;
    });
  };

  const renderBasicProtocols = () => {
    const basicProtocols = Object.values(protocols.app.basic);
    const orderedBasicProtocols = getValidProtocols(basicProtocols, hospitalProtocols);
    const filteredProtocolsByPlatform = filterProtocolsByPlatform(orderedBasicProtocols);

    if (filteredProtocolsByPlatform.length) {
      return (
        <>
          <PrescriptionsHeader
            software={software}
            activityType="prescriptions_basicActivities"
            platformFilter={platformFilter}
            setPlatformFilter={setPlatformFilter}
          />
          {filteredProtocolsByPlatform.map((orderedProtocol) => {
            const protocolId = orderedProtocol.PROTOCOL_ID;
            const protocolPrescriptions = getProtocolPrescriptions(protocolId);
            const protocolSessions = protocolsSessionsNumber.filter((protocol) => protocol.PROTOCOL_ID === protocolId)[0];
            const sessionsNumber = protocolSessions ? protocolSessions.sessionsNumber : 0;
            const protocolMinutesUsage = minutesUsage.hasOwnProperty(protocolId) ? `${minutesUsage[protocolId]}%` : "-";
            const { img, name, video, description } = GetAppBasicProtocolData(protocolId);
            const filteredProtocolPlatforms = hospitalProtocols.find((protocolPlatform) => protocolPlatform.PROTOCOL_ID === protocolId);
            const validateProtocolPlatforms = filteredProtocolPlatforms.hasOwnProperty("PLATFORMS")
              ? filteredProtocolPlatforms.PLATFORMS
              : [];

            return (
              <ProtocolPrescription
                key={protocolId}
                protocolId={protocolId}
                protocolImg={img}
                protocolName={t(`protocols_${name}`)}
                prescriptions={protocolPrescriptions}
                prescriptionsToDatabase={prescriptionsToDatabase}
                isDisabledInputs={isDisabledInputs}
                patientId={patientId}
                software={software}
                sessionsNumber={sessionsNumber}
                minutesUsage={protocolMinutesUsage}
                videoLink={t(`protocols_${video}`)}
                protocolDescription={t(`protocols_${description}`)}
                protocolPlatforms={validateProtocolPlatforms}
              />
            );
          })}
        </>
      );
    }
  };

  const renderRotationProtocols = () => {
    const rotationProtocols = Object.values(protocols.app.rotation);
    const orderedRotationProtocols = getValidProtocols(rotationProtocols, hospitalProtocols);
    const filteredProtocolsByPlatform = filterProtocolsByPlatform(orderedRotationProtocols);

    if (filteredProtocolsByPlatform.length) {
      return (
        <>
          <PrescriptionsHeader software={software} activityType="prescriptions_rotationActivities" />
          {filteredProtocolsByPlatform.map((orderedProtocol) => {
            const protocolId = orderedProtocol.PROTOCOL_ID;
            const protocolPrescriptions = getProtocolPrescriptions(protocolId);
            const protocolSessions = protocolsSessionsNumber.filter((protocol) => protocol.PROTOCOL_ID === protocolId)[0];
            const sessionsNumber = protocolSessions ? protocolSessions.sessionsNumber : 0;
            const protocolMinutesUsage = minutesUsage.hasOwnProperty(protocolId) ? `${minutesUsage[protocolId]}%` : "-";
            const { img, name, video, description } = GetAppRotationProtocolData(protocolId);
            const filteredProtocolPlatforms = hospitalProtocols.find((protocolPlatform) => protocolPlatform.PROTOCOL_ID === protocolId);
            const validateProtocolPlatforms = filteredProtocolPlatforms.hasOwnProperty("PLATFORMS")
              ? filteredProtocolPlatforms.PLATFORMS
              : [];

            return (
              <ProtocolPrescription
                key={protocolId}
                protocolId={protocolId}
                protocolImg={img}
                protocolName={t(`protocols_${name}`)}
                prescriptions={protocolPrescriptions}
                prescriptionsToDatabase={prescriptionsToDatabase}
                isDisabledInputs={isDisabledInputs}
                patientId={patientId}
                software={software}
                sessionsNumber={sessionsNumber}
                minutesUsage={protocolMinutesUsage}
                videoLink={t(`protocols_${video}`)}
                protocolDescription={t(`protocols_${description}`)}
                protocolPlatforms={validateProtocolPlatforms}
              />
            );
          })}
        </>
      );
    }
  };

  const renderARProtocols = () => {
    const arProtocols = Object.values(protocols.app.ar);
    const orderedARProtocols = getValidProtocols(arProtocols, hospitalProtocols);
    const filteredProtocolsByPlatform = filterProtocolsByPlatform(orderedARProtocols);

    if (filteredProtocolsByPlatform.length) {
      return (
        <>
          <PrescriptionsHeader software={software} activityType="prescriptions_arActivities" isARSection />
          {filteredProtocolsByPlatform.map((orderedProtocol) => {
            const protocolId = orderedProtocol.PROTOCOL_ID;
            const protocolPrescriptions = getProtocolPrescriptions(protocolId);
            const protocolSessions = protocolsSessionsNumber.filter((protocol) => protocol.PROTOCOL_ID === protocolId)[0];
            const sessionsNumber = protocolSessions ? protocolSessions.sessionsNumber : 0;
            const protocolMinutesUsage = minutesUsage.hasOwnProperty(protocolId) ? `${minutesUsage[protocolId]}%` : "-";
            const { img, name, video, description } = GetAppARProtocolData(protocolId);
            const filteredProtocolPlatforms = hospitalProtocols.find((protocolPlatform) => protocolPlatform.PROTOCOL_ID === protocolId);
            const validateProtocolPlatforms = filteredProtocolPlatforms.hasOwnProperty("PLATFORMS")
              ? filteredProtocolPlatforms.PLATFORMS
              : [];

            return (
              <ProtocolPrescription
                key={protocolId}
                protocolId={protocolId}
                protocolImg={img}
                protocolName={t(`protocols_${name}`)}
                prescriptions={protocolPrescriptions}
                prescriptionsToDatabase={prescriptionsToDatabase}
                isDisabledInputs={isDisabledInputs}
                patientId={patientId}
                software={software}
                sessionsNumber={sessionsNumber}
                minutesUsage={protocolMinutesUsage}
                videoLink={t(`protocols_${video}`)}
                protocolDescription={t(`protocols_${description}`)}
                protocolPlatforms={validateProtocolPlatforms}
                isIncompatibleWithAR={isIncompatibleWithAR}
                protocolWithSupport={protocolsSupportCompatibility.includes(protocolId)}
              />
            );
          })}
        </>
      );
    }
  };

  const renderHandTrackingProtocols = () => {
    const handTrackingProtocols = Object.values(protocols.web.handTracking);
    const orderedWebProtocols = getValidProtocols(handTrackingProtocols, hospitalProtocols);
    const filteredProtocolsByPlatform = filterProtocolsByPlatform(orderedWebProtocols);

    if (filteredProtocolsByPlatform.length) {
      return (
        <>
          <PrescriptionsHeader software={software} activityType="prescriptions_handTrackingActivities" />
          {filteredProtocolsByPlatform.map((orderedProtocol) => {
            const protocolId = orderedProtocol.PROTOCOL_ID;
            const protocolPrescriptions = getProtocolPrescriptions(protocolId);
            const protocolSessions = protocolsSessionsNumber.filter((protocol) => protocol.PROTOCOL_ID === protocolId)[0];
            const sessionsNumber = protocolSessions ? protocolSessions.sessionsNumber : 0;
            const { img, name, video, description } = GetHandTrackingProtocolData(protocolId);
            const filteredProtocolPlatforms = hospitalProtocols.find((protocolPlatform) => protocolPlatform.PROTOCOL_ID === protocolId);
            const validateProtocolPlatforms = filteredProtocolPlatforms.hasOwnProperty("PLATFORMS")
              ? filteredProtocolPlatforms.PLATFORMS
              : [];

            return (
              <ProtocolPrescription
                key={protocolId}
                protocolId={protocolId}
                protocolImg={img}
                protocolName={t(`protocols_${name}`)}
                prescriptions={protocolPrescriptions}
                isDisabledInputs={isDisabledInputs}
                prescriptionsToDatabase={prescriptionsToDatabase}
                patientId={patientId}
                software={software}
                sessionsNumber={sessionsNumber}
                videoLink={t(`protocols_${video}`)}
                protocolDescription={t(`protocols_${description}`)}
                protocolPlatforms={validateProtocolPlatforms}
              />
            );
          })}
        </>
      );
    }
  };

  const renderFullBodyProtocols = () => {
    const fullBodyProtocols = Object.values(protocols.web.fullBody);
    const orderedWebProtocols = getValidProtocols(fullBodyProtocols, hospitalProtocols);
    const filteredProtocolsByPlatform = filterProtocolsByPlatform(orderedWebProtocols);

    if (filteredProtocolsByPlatform.length) {
      return (
        <>
          <PrescriptionsHeader software={software} activityType="prescriptions_fullBodyActivities" />
          {filteredProtocolsByPlatform.map((orderedProtocol) => {
            const protocolId = orderedProtocol.PROTOCOL_ID;
            const protocolPrescriptions = getProtocolPrescriptions(protocolId);
            const protocolSessions = protocolsSessionsNumber.filter((protocol) => protocol.PROTOCOL_ID === protocolId)[0];
            const sessionsNumber = protocolSessions ? protocolSessions.sessionsNumber : 0;
            const { img, name, video, description } = GetFullBodyProtocolData(protocolId);
            const filteredProtocolPlatforms = hospitalProtocols.find((protocolPlatform) => protocolPlatform.PROTOCOL_ID === protocolId);
            const validateProtocolPlatforms = filteredProtocolPlatforms.hasOwnProperty("PLATFORMS")
              ? filteredProtocolPlatforms.PLATFORMS
              : [];

            const protocolControlsMapping = {
              [protocols.web.fullBody.TWISTER_BUDDY.id]: {
                protocolControlsToSave: protocolControlsActions.twisterBuddyProtocolControls,
                setProtocolControlsToSave: protocolControlsActions.setTwisterBuddyProtocolControls,
                protocolControlsErrors: protocolControlsActions.twisterBuddyControlsErrors,
                setProtocolControlsErrors: protocolControlsActions.setTwisterBuddyControlsErrors,
              },
              [protocols.web.fullBody.COSTUME_PARTY.id]: {
                protocolControlsToSave: protocolControlsActions.costumePartyProtocolControls,
                setProtocolControlsToSave: protocolControlsActions.setCostumePartyProtocolControls,
                protocolControlsErrors: protocolControlsActions.costumePartyControlErrors,
                setProtocolControlsErrors: protocolControlsActions.setCostumePartyControlErrors,
              },
              [protocols.web.fullBody.GENTLE_GIANT.id]: {
                protocolControlsToSave: protocolControlsActions.gentleGiantProtocolControls,
                setProtocolControlsToSave: protocolControlsActions.setGentleGiantProtocolControls,
                protocolControlsErrors: protocolControlsActions.gentleGiantControlErrors,
                setProtocolControlsErrors: protocolControlsActions.setGentleGiantControlErrors,
              },
            };
            const protocolControls = protocolControlsMapping[protocolId] || {};

            return (
              <ProtocolPrescription
                key={protocolId}
                protocolId={protocolId}
                protocolImg={img}
                protocolName={t(`protocols_${name}`)}
                prescriptions={protocolPrescriptions}
                isDisabledInputs={isDisabledInputs}
                prescriptionsToDatabase={prescriptionsToDatabase}
                patientId={patientId}
                software={software}
                sessionsNumber={sessionsNumber}
                videoLink={t(`protocols_${video}`)}
                protocolDescription={t(`protocols_${description}`)}
                protocolPlatforms={validateProtocolPlatforms}
                protocolControlsToSave={protocolControls.protocolControlsToSave}
                setProtocolControlsToSave={protocolControls.setProtocolControlsToSave}
                protocolControlsErrors={protocolControls.protocolControlsErrors}
                setProtocolControlsErrors={protocolControls.setProtocolControlsErrors}
                rechargeProtocolControls={rechargeProtocolControls}
                setRechargeProtocolControls={setRechargeProtocolControls}
              />
            );
          })}
        </>
      );
    }
  };

  return (
    <>
      {renderBasicProtocols()}
      {renderRotationProtocols()}
      {renderARProtocols()}
      {renderHandTrackingProtocols()}
      {renderFullBodyProtocols()}
    </>
  );
};

export default PlusProtocols;
